import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import ViolationCanvas from '../components/ViolationCanvas';
import './AnnotationPage.css';
import { useApiMutation } from '../hooks/useApi';
import { violationApi } from '../services/api';
import { resolveAnnotationBackNavigation } from '../utils/resultsReturnNavigation';
import { decodeUrlForDisplay } from '../utils/urlDisplay';
import { useAuth } from '../auth/AuthContext';
import type { ViolationData, ViolationScope } from '../types/violation';
import { mapViolationFromApi, normalizeMultilineText } from '../utils/violationFixability';
import { useToast } from '../components/toast/ToastProvider';

const AnnotationPage: React.FC = () => {
    const { jobId, urlId } = useParams<{ jobId?: string; urlId?: string }>();
    const [searchParams] = useSearchParams();
    const { session } = useAuth();
    const toast = useToast();

    const finalJobId = jobId || searchParams.get('jobId') || undefined;
    const finalUrlId = urlId || searchParams.get('urlId') || undefined;
    const initialSelectedViolationId = searchParams.get('violationId') || undefined;
    const violationIdsParam = searchParams.get('violationIds') || undefined;
    const readOnly = searchParams.get('readOnly') === '1';
    const evaluateOutcomeKind = searchParams.get('evaluate_outcome_kind') || undefined;
    const includeDeleted = Boolean(evaluateOutcomeKind);
    const [violations, setViolations] = useState<ViolationData[]>([]);
    const [currentViolations, setCurrentViolations] = useState<ViolationData[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [apiResponse, setApiResponse] = useState<any>(null);
    const [imageUrl, setImageUrl] = useState<string>('');
    const [categoryContentExistenceDict, setCategoryContentExistenceDict] = useState<Record<string, boolean>>({});
    const [violationCategories, setViolationCategories] = useState<Array<{ id: string; title: string }>>([]);
    const [pageUnfixableRuleDescription, setPageUnfixableRuleDescription] = useState<string | undefined>();
    const [enableUnfixableClassification, setEnableUnfixableClassification] = useState(false);

    const reviewerUsername =
        session?.user?.username ||
        session?.user?.email ||
        'user';

    const { mutate: markAsReviewed, loading: reviewLoading, error: reviewError } = useApiMutation(
        (params: { jobId: string; urlId: string; notes?: string }) =>
            violationApi.markReviewed(params.jobId, params.urlId, params.notes)
    );

    const { mutate: saveViolations, loading: saveLoading, error: saveError } = useApiMutation(
        (params: { jobId: string; urlId: string; violations: any[]; category_content_existence_dict?: Record<string, boolean> }) =>
            violationApi.bulkSaveViolations(params.jobId, params.urlId, params.violations, params.category_content_existence_dict)
    );

    useEffect(() => {
        if (finalJobId && finalUrlId) {
            setLoading(true);
            setError(null);

            violationApi.getViolations(finalJobId, finalUrlId)
                .then((result) => {
                    setApiResponse(result);

                    if (result && typeof result === 'object') {
                        const apiResult = result as any;
                        const pngUrl = apiResult.png_url || (finalJobId && finalUrlId ? `/pngs/job_${finalJobId}/${finalUrlId}.png` : '');
                        const fullImageUrl = pngUrl.startsWith('http')
                            ? pngUrl
                            : `${process.env.REACT_APP_API_BASE_URL}${pngUrl}`;
                        setImageUrl(fullImageUrl);

                        if (apiResult.violations && Array.isArray(apiResult.violations)) {
                            const mappedViolations: ViolationData[] = apiResult.violations.map((violation: any) => {
                                const xRatio = typeof violation.x_ratio === 'number' ? violation.x_ratio : undefined;
                                const yRatio = typeof violation.y_ratio === 'number' ? violation.y_ratio : undefined;
                                const widthRatio = typeof violation.width_ratio === 'number' ? violation.width_ratio : undefined;
                                const heightRatio = typeof violation.height_ratio === 'number' ? violation.height_ratio : undefined;
                                const hasPosition = (
                                    typeof xRatio === 'number'
                                    && typeof yRatio === 'number'
                                    && typeof widthRatio === 'number'
                                    && typeof heightRatio === 'number'
                                );

                                return {
                                    id: violation.id,
                                    xRatio,
                                    yRatio,
                                    widthRatio,
                                    heightRatio,
                                    violationContent: violation.violation_content || '',
                                    editNote: violation.edit_note || '',
                                    captureUrl: violation.capture_url || '',
                                    category: violation.category || '',
                                    confidence: violation.confidence || 0,
                                    violationType: violation.violation_type || 'generated',
                                    scope: (violation.scope as ViolationScope) || (hasPosition ? 'specific_content' : 'overall_page'),
                                    severity: 'medium',
                                    createdAt: violation.created_at || new Date().toISOString(),
                                    createdBy: violation.created_by || 'user',
                                    deleted: violation.deleted,
                                    evaluateOutcomeKind: violation.evaluate_outcome_kind || undefined,
                                    categoryContentExistence: violation.category_content_existence ?? false,
                                    categoryContentViolationExistence: violation.category_content_violation_existence ?? false,
                                    ...mapViolationFromApi(violation),
                                };
                            });

                            const violationIdFilter = violationIdsParam
                                ? violationIdsParam
                                      .split(',')
                                      .map((id) => id.trim())
                                      .filter(Boolean)
                                : initialSelectedViolationId
                                  ? [initialSelectedViolationId]
                                  : null;

                            const filtered = violationIdFilter
                                ? mappedViolations.filter((v) => violationIdFilter.includes(v.id))
                                : evaluateOutcomeKind
                                  ? mappedViolations.filter(
                                        (v) => v.evaluateOutcomeKind === evaluateOutcomeKind
                                    )
                                  : mappedViolations;
                            setViolations(filtered);
                        } else {
                            setViolations([]);
                        }

                        if (apiResult.category_content_existence_dict) {
                            setCategoryContentExistenceDict(apiResult.category_content_existence_dict);
                        } else {
                            setCategoryContentExistenceDict({});
                        }

                        if (apiResult.violation_categories && Array.isArray(apiResult.violation_categories)) {
                            setViolationCategories(apiResult.violation_categories);
                        } else {
                            setViolationCategories([]);
                        }

                        const enableUnfixable =
                            apiResult.enable_unfixable_classification
                            ?? apiResult.enableUnfixableClassification
                            ?? false;
                        setEnableUnfixableClassification(Boolean(enableUnfixable));

                        const pageRuleDesc =
                            apiResult.page_unfixable_rule_description
                            ?? apiResult.pageUnfixableRuleDescription;
                        setPageUnfixableRuleDescription(
                            enableUnfixable
                                && typeof pageRuleDesc === 'string'
                                && pageRuleDesc.trim()
                                ? normalizeMultilineText(pageRuleDesc)
                                : undefined
                        );
                    } else {
                        setViolations([]);
                    }
                    setLoading(false);
                })
                .catch((err) => {
                    console.error('API fetch failed:', err);
                    setError('違反データの取得に失敗しました');
                    setViolations([]);
                    setLoading(false);
                });
        } else {
            setError('ジョブIDまたはURL IDが指定されていません');
            setViolations([]);
        }
    }, [finalJobId, finalUrlId, violationIdsParam, evaluateOutcomeKind, initialSelectedViolationId]);

    useEffect(() => {
        setCurrentViolations(violations);
    }, [violations]);

    const handleViolationsChange = (nextViolations: ViolationData[], _deletedIds: string[]) => {
        setCurrentViolations(nextViolations);
    };

    const transformViolationsForApi = (source: ViolationData[]) => {
        const nowIso = new Date().toISOString();

        return source.map((violation) => {
            const finalType = violation.violationType || 'manual_created';
            const hasPosition = (
                typeof violation.xRatio === 'number'
                && typeof violation.yRatio === 'number'
                && typeof violation.widthRatio === 'number'
                && typeof violation.heightRatio === 'number'
            );
            const scope: ViolationScope = violation.scope || (hasPosition ? 'specific_content' : 'overall_page');
            const isManualFixability = violation.fixabilitySource === 'manual';

            const payload: Record<string, unknown> = {
                id: violation.id,
                job_id: finalJobId,
                url_id: finalUrlId,
                x_ratio: violation.xRatio,
                y_ratio: violation.yRatio,
                width_ratio: violation.widthRatio,
                height_ratio: violation.heightRatio,
                violation_content: scope === 'overall_page' ? null : violation.violationContent,
                edit_note: violation.editNote || '',
                capture_url: scope === 'off_capture' ? (violation.captureUrl || null) : null,
                category: violation.category,
                deleted: violation.deleted || false,
                category_content_existence: violation.categoryContentExistence ?? false,
                category_content_violation_existence: violation.categoryContentViolationExistence ?? false,
                severity: 'medium',
                violation_type: finalType,
                scope,
                fixability: violation.fixability ?? 'fixable',
                unfixable_domain: violation.unfixableDomain ?? null,
                unfixable_rule_description: null,
                unfixable_reason_description_part:
                    violation.unfixableReasonDescriptionPart ?? null,
                fixability_classified_at: violation.fixabilityClassifiedAt ?? null,
                fixability_source: violation.fixabilitySource ?? null,
            };

            if (isManualFixability) {
                payload.fixability_source = 'manual';
                payload.fixability_overridden_by = violation.fixabilityOverriddenBy || reviewerUsername;
                payload.fixability_overridden_at = violation.fixabilityOverriddenAt || nowIso;
            }

            return payload;
        });
    };

    const handleSaveViolations = async () => {
        if (!finalJobId || !finalUrlId) return;

        const sourceViolations = currentViolations.length > 0 ? currentViolations : violations;

        try {
            await saveViolations({
                jobId: finalJobId,
                urlId: finalUrlId,
                violations: transformViolationsForApi(sourceViolations),
                category_content_existence_dict: categoryContentExistenceDict,
            });
            toast.success('違反データを保存しました', { title: '保存完了' });
        } catch (err) {
            console.error('Failed to save violations:', err);
            toast.error('違反データの保存に失敗しました', { title: '保存失敗' });
        }
    };

    const handleMarkAsReviewed = async () => {
        if (!finalJobId || !finalUrlId) return;

        try {
            const sourceViolations = currentViolations.length > 0 ? currentViolations : violations;
            await saveViolations({
                jobId: finalJobId,
                urlId: finalUrlId,
                violations: transformViolationsForApi(sourceViolations),
                category_content_existence_dict: categoryContentExistenceDict,
            });
            await markAsReviewed({
                jobId: finalJobId,
                urlId: finalUrlId,
                notes: 'Reviewed via annotation page',
            });
            toast.success('違反データを保存し、レビュー済みにしました', { title: 'レビュー完了' });
        } catch (err) {
            console.error('Failed to save and mark as reviewed:', err);
            toast.error('保存またはレビューに失敗しました', { title: '処理失敗' });
        }
    };

    const jobDisplayName =
        (apiResponse && (apiResponse.job_name || apiResponse.jobName)) ||
        (finalJobId ? String(finalJobId) : undefined);
    const pageUrlForSidebar =
        (apiResponse && typeof apiResponse === 'object' && (apiResponse as any).url)
            ? String((apiResponse as any).url)
            : undefined;

    const returnToRaw = searchParams.get('returnTo');
    const backNavigation = useMemo(
        () => resolveAnnotationBackNavigation({ returnToRaw, jobId: finalJobId }),
        [returnToRaw, finalJobId]
    );

    return (
        <div className="annotation-page">
            <div className="annotation-breadcrumbs">
                <Link className="annotation-breadcrumbs__back" to={backNavigation.href}>
                    <span aria-hidden>←</span>
                    <span>{backNavigation.label}</span>
                </Link>
                <div className="annotation-breadcrumbs__meta">
                    {jobDisplayName && (
                        <div className="annotation-breadcrumbs__job" title={jobDisplayName}>
                            <span className="annotation-breadcrumbs__jobLabel">ジョブ名:</span>{' '}
                            <span className="annotation-breadcrumbs__jobValue">{jobDisplayName}</span>
                        </div>
                    )}
                    {pageUrlForSidebar && (
                        <div className="annotation-breadcrumbs__url">
                            <span className="annotation-breadcrumbs__urlLabel">URL:</span>{' '}
                            <a
                                href={decodeUrlForDisplay(pageUrlForSidebar)}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={decodeUrlForDisplay(pageUrlForSidebar)}
                            >
                                {decodeUrlForDisplay(pageUrlForSidebar)}
                            </a>
                        </div>
                    )}
                </div>
            </div>
            <div className="annotation-content">
                <ViolationCanvas
                    violations={violations}
                    categoryContentExistenceDict={categoryContentExistenceDict}
                    onCategoryContentExistenceDictChange={setCategoryContentExistenceDict}
                    violationCategories={violationCategories}
                    imageUrl={imageUrl || `${process.env.REACT_APP_API_BASE_URL}/pngs/job_${finalJobId}/${finalUrlId}.png`}
                    jobId={finalJobId}
                    urlId={finalUrlId}
                    jobName={jobDisplayName}
                    pageUrl={pageUrlForSidebar}
                    resultsListHref={backNavigation.href}
                    pageUnfixableRuleDescription={pageUnfixableRuleDescription}
                    enableUnfixableClassification={enableUnfixableClassification}
                    initialSelectedViolationId={initialSelectedViolationId}
                    readOnly={readOnly}
                    includeDeleted={includeDeleted}
                    onViolationsChange={readOnly ? undefined : handleViolationsChange}
                    onSave={readOnly ? undefined : handleSaveViolations}
                    onMarkReviewed={readOnly ? undefined : handleMarkAsReviewed}
                    saveLoading={readOnly ? false : saveLoading}
                    reviewLoading={readOnly ? false : reviewLoading}
                    errorText={error || reviewError || saveError}
                />
            </div>
        </div>
    );
};

export default AnnotationPage;
